import React, { useState } from "react";
import { motion } from "framer-motion";
import { Check, Clock, Trash2 } from "lucide-react";
import { Notifications } from "@/integration/application/interfaces/notification.interface";
import { formatDate } from "../utils/renderers";

interface NotificationFooterProps {
  notification: Notifications;
  onMarkAsRead?: (id: string) => void;
  onDelete?: (id: string) => void;
  compact?: boolean;
}

/**
 * NotificationFooter component
 * Displays the timestamp and action buttons (mark as read, delete)
 */
const NotificationFooter: React.FC<NotificationFooterProps> = React.memo(
  ({ notification, onMarkAsRead, onDelete, compact = false }) => {
    const [isLoading, setIsLoading] = useState({
      markAsRead: false,
      delete: false,
    });

    const handleMarkAsRead = async () => {
      if (!onMarkAsRead || notification.isRead) return;

      setIsLoading({ ...isLoading, markAsRead: true });
      try {
        await onMarkAsRead(notification.id);
      } catch (error) {
        console.error("Error marking notification as read:", error);
      } finally {
        setIsLoading({ ...isLoading, markAsRead: false });
      }
    };

    const handleDelete = async () => {
      if (!onDelete) return;

      setIsLoading({ ...isLoading, delete: true });
      try {
        await onDelete(notification.id);
      } catch (error) {
        console.error("Error deleting notification:", error);
      } finally {
        setIsLoading({ ...isLoading, delete: false });
      }
    };

    return (
      <div
        className={`flex ${compact ? "justify-between items-center mt-2" : "items-center justify-between mt-6"}`}
      >
        {/* Timestamp */}
        {compact ? (
          <span className="text-xs text-gray-500 block">
            {notification?.createAt
              ? formatDate(notification.createAt)
              : "Data não disponível"}
          </span>
        ) : (
          <div className="flex items-center gap-2 text-xs text-gray-400 bg-gray-800/40 px-3 py-1.5 rounded-full border border-gray-700/50">
            <Clock size={14} className="text-red-400" />
            <span>
              {notification?.createAt
                ? formatDate(notification.createAt)
                : "Data não disponível"}
            </span>
          </div>
        )}

        {/* Action buttons */}
        <div className="flex items-center gap-3">
          {/* Mark as read button */}
          {!notification.isRead && onMarkAsRead && (
            <motion.button
              whileHover={{ scale: compact ? 1 : 1.05 }}
              whileTap={{ scale: compact ? 0.98 : 0.95 }}
              onClick={handleMarkAsRead}
              disabled={isLoading.markAsRead}
              className={`flex items-center gap-2 ${
                compact
                  ? "text-xs text-red-400 hover:text-red-300 transition-colors bg-gradient-to-b from-gray-800/60 to-gray-900/60 px-2 py-0.5 rounded-md border border-gray-700/50 hover:border-red-500/30"
                  : "px-4 py-2 rounded-xl bg-gradient-to-r from-gray-800/60 to-gray-900/60 text-sm text-gray-300 hover:text-white transition-all duration-300 border border-gray-700/50 hover:border-red-500/30 shadow-md"
              } ${isLoading.markAsRead ? "opacity-70" : ""}`}
              aria-busy={isLoading.markAsRead}
            >
              {!compact && <Check size={14} className="text-red-400" />}
              <span>
                {isLoading.markAsRead ? "Processando..." : "Marcar como lida"}
              </span>
            </motion.button>
          )}

          {/* Delete button */}
          {!compact && onDelete && (
            <motion.button
              whileHover={{ scale: 1.1 }}
              whileTap={{ scale: 0.9 }}
              onClick={handleDelete}
              disabled={isLoading.delete}
              className={`flex items-center justify-center w-10 h-10 rounded-full bg-gradient-to-r from-gray-800/60 to-gray-900/60 text-gray-400 hover:text-red-400 transition-all duration-300 border border-gray-700/50 hover:border-red-500/30 shadow-md ${isLoading.delete ? "opacity-70" : ""}`}
              aria-busy={isLoading.delete}
              aria-label="Delete notification"
            >
              <Trash2 size={16} />
            </motion.button>
          )}
        </div>
      </div>
    );
  }
);

NotificationFooter.displayName = "NotificationFooter";

export default NotificationFooter;